import type { UiStrings } from "../types";

export const en: UiStrings = {
  nav: {
    category: "Category",
    forYourWork: "For Your Work",
    searchPlaceholder: "Search 300+ tools...",
    heroPlaceholder: "What's on your mind?",
  },
  trustBadges: {
    noLogin: "No login needed to use the tools",
    freeUsesDaily: "10 trial uses daily",
    filesNeverStored: "Files never stored",
    worksAnyDevice: "Works on any device",
    worksWhenAiBlocked: "Works under any company AI policy",
    availableInLanguages: "Available in multiple languages",
  },
  common: {
    backToHome: "Back to Home",
    home: "Home",
    signIn: "Sign In",
    account: "Account",
    contact: "Contact",
    blog: "Blog",
    shareThisTool: "Share this tool",
    download: "Download",
    copy: "Copy",
    copied: "Copied",
  },
  footer: {
    tagline: "Your all-in-one online productivity platform. 300+ professional tools, premium applications and workflow products for every task.",
    company: "Company",
    about: "About",
    blog: "Blog",
    contact: "Contact",
    pricing: "Pricing",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    allTools: "All Tools",
    allRightsReserved: "All rights reserved.",
  },
  contact: {
    title: "Contact Us",
    subtitle: "Questions, feedback, or something not working right? We're happy to help.",
    email: "Email",
    responseTime: "Response Time",
    responseTimeValue: "We typically respond within 1-2 business days.",
    sendEmail: "Send us an email",
    billingNote: "For billing questions, please include the email address associated with your account.",
    whoWeAreTitle: "Who runs FreeDailyPro",
    whoWeAreBody: "FreeDailyPro is operated by DreamUSA LLC, a small, independently run software business. There's no large support team behind this — the person who reads and replies to your email is also the person who builds and maintains the tools themselves, which is also why a reply sometimes takes a day or two rather than being instant.",
    whatWeSupportTitle: "What we can help with",
    whatWeSupportBody: "Bug reports for any of the 300+ tools, feature requests, billing and subscription questions (including cancellations and refund requests), account and login issues, and general feedback on what's working well or not. If a tool produced an incorrect result, including the file type and roughly what went wrong helps get it fixed faster.",
  },
  home: {
    heroTitleLine1: "Every Professional Task.",
    heroTitleLine2: "One Browser Platform",
    heroSubtitle: "Use a tool. Run an application. Start a workflow product. Build with API & MCP. Or have us build it for you.",
    localSignalUsedTitle: "Used on this device",
    localSignalTryTitle: "Try one of these",
    localSignalBadge: "Local \u00b7 No network",
    localSignalUsedDesc: "tools used on this device \u2014 none of it left your browser",
    localSignalTryDesc: "tools used yet on this device \u2014 everything runs right here in your browser",
    corporateSafeTitle: "Corporate-Safe",
    corporateSafeSubtitle: "Whatever your company's AI policy is, FreeDailyPro works the same",
    corporateSafeBlockedTitle: "Fully blocked",
    corporateSafeBlockedDesc: "No AI tools of any kind allowed on the network. Every FreeDailyPro tool still works \u2014 nothing is ever uploaded to a server.",
    corporateSafeGovernedTitle: "Governed access",
    corporateSafeGovernedDesc: "AI is allowed through approved, vetted tools only. Your files never leave the browser, so there is no file-handling service to vet. (Search may use an AI language model for complex queries; it never sees your files.)",
    corporateSafeOpenTitle: "No restrictions",
    corporateSafeOpenDesc: "Use whatever gets the job done. FreeDailyPro is just faster, since there's nothing to upload or wait on.",
    corporateSafeCta: "See the full corporate-safe breakdown \u2192",
    exploreToolkitTitle: "Explore the toolkit",
    exploreToolkitSubtitle: "300+ tools across {catCount} categories \u2014 pick one, or just search.",
    forYourWorkTitle: "Built for how you actually work",
    forYourWorkSubtitle: "Same professional tools, organized around your actual job",
    exploreTools: "Explore tools",
    seeAllRoles: "See all {count} roles \u2192",
    blogTitle: "From the Blog",
    viewAllPosts: "View All Posts",
    faqTitle: "Have a question? Check our FAQ",
    faqMoreLink: "Looking for more answers? Browse the full FAQ \u2192",
    toolkitProseTitle: "A professional toolkit built for daily work, not just one-off tasks",
    closingCtaTitle: "Don't forget to add the shortcut",
    closingCtaDesc: "Add it once and it's always a click away \u2014 install the desktop shortcut or add it to your phone's home screen and FreeDailyPro opens like a native app, no browser tab hunting required.",
    categoriesLabel: "{count} CATEGORIES \u2192",
    viewAllCategoriesLabel: "VIEW ALL {count} CATEGORIES \u2192",
    yourFavorites: "Your Favorites",
    recentlyUsed: "Recently Used",
    toolkitProsePara1: "FreeDailyPro brings together 300+ professional tools, premium applications and workflow products: PDF editing, image conversion, video trimming, audio cleanup, text formatting and dozens of calculators, in one place, so you're not bouncing between a different site for every task.",
    toolkitProsePara2: "Every tool runs entirely in your browser. When you compress a PDF or remove the background from a photo, that file never leaves your device \u2014 it's not uploaded, processed on a server, or stored anywhere, even temporarily. That's not a limitation, it's the whole design: faster results, and nothing to worry about if the file is sensitive.",
    toolkitProsePara3: "That local-first approach also means FreeDailyPro works the same whether you're on a personal laptop, a locked-down work machine, or a network with strict AI usage policies \u2014 your files never go to an external server for IT to evaluate. (The search box can use an AI language model for complex queries; it never sees your files.)",
    toolkitProsePara4: "Whether you need to merge PDFs for a report, resize an image for a listing, trim a video clip for social media, or run a quick mortgage or tax calculation, FreeDailyPro is built to be the tool you reach for daily \u2014 not just the one you remember exists when you happen to need it.",
    heroLaneToolsTitle: "Professional tools for individual tasks",
    heroLaneToolsDesc: "PDF, image, video & more — one action, done in your browser.",
    heroLaneToolsCta: "Browse tools",
    heroLanePremiumTitle: "Premium applications for bigger jobs",
    heroLanePremiumDesc: "Multi-step apps: documents, forms, planners, print & event paperwork.",
    heroLanePremiumCta: "Browse applications",
    heroLaneWorkflowTitle: "Workflow products",
    heroLaneWorkflowDesc: "Book Writing Toolbox, Event Suite, Seller Studio, Legal Production Desk. Several steps, one result. Each has its own pricing.",
    heroLaneWorkflowCta: "See workflow products",
    heroLaneApiTitle: "API & MCP",
    heroLaneApiDesc: "Call the same tools from your own code or AI agent.",
    heroLaneApiCta: "Open developers",
    heroLaneCustomTitle: "Custom software",
    heroLaneCustomDesc: "Tell us what you need. We build it for a one-time fee, then you pay a small monthly usage fee.",
    heroLaneCustomCta: "Request a build",
  },
  install: {
    desktopTitle: "Desktop Shortcut",
    desktopSubtitle: "One click, opens instantly",
    mobileTitle: "Mobile App",
    mobileSubtitle: "Add to your home screen",
    addToHomeScreenTitle: "Add FreeDailyPro to your Home Screen",
    iosInstructions: "Tap the Share icon in Safari, then choose \"Add to Home Screen.\"",
    genericInstructions: "Open your browser's menu and look for \"Add to Home Screen\" or \"Install App.\"",
    desktopUnavailableTitle: "One-click install isn't available in this browser",
    desktopUnavailableDesc: "Look for the install icon in your browser's address bar, or open the browser menu and choose \"Install page as app\" (Chrome/Edge) — both create a real, standalone app for this site.",
    desktopSuccess: "Shortcut created! Look for the FreeDailyPro icon.",
    mobileSuccess: "Added! Look for the FreeDailyPro icon on your home screen.",
  },
  toolPage: {
    proBadge: "Pro",
    usesRemainingToday: "{count} trial uses left today",
    whatThisToolDoes: "What this tool does",
    proTips: "Pro tips",
    continueWith: "Continue with",
    moreFromCategory: "More from this category",
  },
  usageStrip: {
    freeUsesPerDay: "Trial: {limit} uses/day",
    remainingToday: "{count} remaining today",
    dayPassLabel: "Ads-free unlimited usage, $1 day pass",
    monthlyLabel: "Ads-free unlimited usage, $4.99 monthly pass",
    quickBuy: "Quick Buy",
  },
  platformsBanner: {
    title: "Where this API actually lives",
    subtitle: "Not a page that says \"coming soon\" — these are live, working listings you can subscribe to or connect to today.",
    postmanDetail: "Ready-to-run requests, fully documented",
    rapidApiDetail: "Subscribe with usage-based billing through Rapid's marketplace",
    mcpRegistryDetail: "Officially listed as com.freedailypro/tools, {mcp} tools, one endpoint",
  },
  pricingBanner: {
    title: "Transparent Pricing",
    subtitle: "No hidden tiers, no surprise limits — exactly what each plan costs and what it gets you.",
    toolPricingTitle: "Usage-based tool pricing",
    apiPricingTitle: "Usage-based API & MCP pricing",
    freeLabel: "Trial",
    freeToolDetail: "10 uses/day on Professional Tools, 2 on Premium Applications",
    dayPassDetail: "$1/day — ad-free, unlimited usage",
    monthlyDetail: "$4.99/month — ad-free, unlimited usage",
    // Real math, not a made-up "save X%": 5 Day Passes = $5, which already
    // costs more than a full month at $4.99. Stated as a break-even fact
    // (accurate at any usage above 5 days/month), not a discount claim.
    monthlySavingsNote: "Cheaper than 5 Day Passes a month",
    freeApiDetail: "20,000 calls/month",
    premiumDetail: "$24/month — 200,000 calls",
    ultraDetail: "$69/month — 750,000 calls",
    megaDetail: "$129/month — 2,000,000 calls + priority",
  },
  apiMcpBanner: {
    title: "Now with API & MCP",
    description: "Call our 200+ tools straight from your own code, or connect an AI agent over MCP — same tools, server-side, for when browser-only isn't enough.",
    oneKey: "One key for everything",
    restApiMcp: "REST API + MCP",
    anyLanguage: "Any language, any server",
    cta: "Explore the API & MCP",
  },
  premiumApps: {
    title: "Premium Applications",
    subtitle: "Real, multi-step applications, not single-action tools — installable on their own too.",
    seeAll: "See all Premium Applications",
  },
  googleDrive: {
    openFromGoogle: "Open from Google Drive",
    saveToGoogle: "Save to Google Drive",
    signingIn: "Opening Google sign-in...",
    fetching: "Getting your file from Google Drive...",
    saving: "Saving to Google Drive...",
    saved: "Saved to Google Drive",
    viewInDrive: "View in Drive",
    popupBlocked: "Your browser blocked the Google sign-in window. Allow pop-ups for this site, then try again.",
    cancelled: "Google sign-in was closed before it finished. Nothing was opened or saved.",
    denied: "Google Drive access was not allowed, so nothing was opened or saved. Try again and allow access to files you choose.",
    loadFailed: "Couldn't reach Google. Check your connection or any content blocker, then try again.",
    network: "The connection to Google Drive dropped. Please try again.",
    tooLarge: "That file is too large to open in the browser.",
    api: "Google Drive couldn't complete that. Please try again.",
    largeFile: "This file is over 50 MB. It may take longer or run out of browser memory.",
    privacyNote: "Your file goes between your browser and Google. It is not sent to our servers.",
  },
};
